import React, { useState, useEffect } from 'react';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Api from '../../../api/global.service';
import BlockUi from 'react-block-ui';
import { formatNumber } from '../../../utils/formatNumber.js';
import EmptyStateChart from '../empty-state-chart';

const ParcelasResumenGenero = ({ id_ciclo }) => {
  const [dataGrafica, setDataGrafica] = useState([]);
  const [blocking, setBlocking] = useState(true);
  const URL_TOTALES_GENERO = 'dashboard/avances/productores_majomut_genero';

  useEffect(() => {
    if (id_ciclo > 0) {
      getData();
    }
  }, [id_ciclo]);

  const getData = async () => {
    setBlocking(true);
    let chartData = [];
    const response = await Api.search(URL_TOTALES_GENERO, {
      filter: [{ column: 'id_ciclo', operator: '=', value: id_ciclo }],
    });
    const data = response.status == 200 && response.data.length > 0 ? response.data : chartData;
    if (data.length > 0) {
      const d = data[0];
      const totalMujer = parseFloat(d.ha_cafe_mujer) + parseFloat(d.ha_cult_mujer);
      const totalHombre = parseFloat(d.ha_cafe_hombre) + parseFloat(d.ha_cult_hombre);
      const totalSA = parseFloat(d.ha_cafe_sin_asignar) + parseFloat(d.ha_cult_sin_asignar);
      const mDataKeyB = totalMujer - parseFloat(d.ha_cafe_mujer);
      const hDataKeyB = totalHombre - parseFloat(d.ha_cafe_hombre);
      const saDataKeyB = totalSA - parseFloat(d.ha_cafe_sin_asignar);
      const haTotal =
        totalSA > totalHombre ? (totalSA > totalMujer ? totalSA : totalMujer) : totalHombre > totalMujer ? totalHombre : totalMujer;

      chartData.push({
        name: 'Mujeres',
        dataKeyA: formatNumber(d.ha_cafe_mujer),
        dataKeyB: mDataKeyB > 8 || mDataKeyB == 0 ? formatNumber(mDataKeyB) : 8,
        dataKeyAB: totalMujer,
        hasData: !(d.ha_cafe_mujer == 0 && d.ha_cult_mujer == 0),
        fillA: '#E0A98F',
        fillB: '#D87748',
        width: formatNumber((totalMujer * 100) / haTotal),
      });

      chartData.push({
        name: 'Hombres',
        dataKeyA: formatNumber(d.ha_cafe_hombre),
        dataKeyB: hDataKeyB > 8 || hDataKeyB == 0 ? formatNumber(hDataKeyB) : 8,
        dataKeyAB: totalHombre,
        hasData: !(d.ha_cafe_hombre == 0 && d.ha_cult_hombre == 0),
        fillA: '#84A96A',
        fillB: '#507736',
        width: formatNumber((totalHombre * 100) / haTotal),
      });

      chartData.push({
        name: 'Sin asignar',
        dataKeyA: formatNumber(d.ha_cafe_sin_asignar),
        dataKeyB: saDataKeyB > 8 || saDataKeyB == 0 ? formatNumber(saDataKeyB) : 8,
        dataKeyAB: totalSA,
        hasData: !(d.ha_cafe_sin_asignar == 0 && d.ha_cult_sin_asignar == 0),
        fillA: '#C4C0C2',
        fillB: '#9C969A',
        width: formatNumber((totalSA * 100) / haTotal),
      });
    }
    setDataGrafica(chartData);
    setBlocking(false);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active) {
      return (
        <div
          style={{
            backgroundColor: 'white',
            borderColor: '#cacaca',
            borderWidth: 1,
            borderStyle: 'solid',
            padding: 5,
            minWidth: 200,
          }}>
          <h5 className="mb-1">{payload[0].payload.name}</h5>
          <p className="mb-1">
            Hectáreas de café cultivadas: <b>{formatNumber(payload[0].payload.dataKeyA)}</b>
          </p>
          <p className="mb-1">
            Hectáreas totales de superficie: <b>{formatNumber(payload[0].payload.dataKeyAB)}</b>
          </p>
        </div>
      );
    }
    return null;
  };

  const dataConValor = dataGrafica.filter(d => d.hasData);
  const totalHaCultivadas = dataConValor.reduce((acc, d) => acc + parseFloat(d.dataKeyA), 0);
  const totalHaSuperficie = dataConValor.reduce((acc, d) => acc + (d.dataKeyAB > 0 ? parseFloat(d.dataKeyAB) : parseFloat(d.dataKeyA)), 0);

  return (
    <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
      <div className="row card-table">
        <h3 className="dashboard-titulo-grafica mb-3">Cultivo y superficie de hombres y mujeres</h3>
        <div className="col-md-12">
          {!blocking && dataConValor.length === 0 && <EmptyStateChart />}
          {dataConValor.length > 0 && (
            <div className="row mt-2 mb-3">
              <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                <div className="font-lato-normal-400" style={{ fontSize: 14 }}>
                  Hectáreas totales cultivadas
                </div>
                <div className="font-lato-normal-600" style={{ fontSize: 32, fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(totalHaCultivadas)}
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div className="font-lato-normal-400" style={{ fontSize: 14 }}>
                  Hectáreas totales de superficie
                </div>
                <div className="font-lato-normal-600" style={{ fontSize: 32, fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(totalHaSuperficie)}
                </div>
              </div>
            </div>
          )}
          {dataConValor.map((data, i) => (
            <React.Fragment key={i}>
              <div className="row mt-2">
                <div className="col-md-12">
                  <h4 className="dashboard-titulo-grafica text-left mb-0" style={{ fontSize: 16, fontWeight: 400 }}>
                    {data.name}
                  </h4>
                  <ResponsiveContainer width={data.width + '%'} height={90}>
                    <BarChart layout="vertical" data={[data]} margin={{ left: -60 }} stackOffset="expand">
                      <CartesianGrid horizontal={false} vertical={false} strokeDasharray="3 3" />
                      <XAxis hide type="number" />
                      <YAxis type="category" dataKey="name" fontSize="14" stroke="#fff" />
                      <Tooltip shared={true} content={<CustomTooltip />} />
                      {data.dataKeyB > 0 ? (
                        <>
                          <Bar
                            dataKey="dataKeyA"
                            stackId="a"
                            fill={data.fillA}
                            name="Hectáreas de café cultivadas"
                            radius={[10, 0, 0, 10]}
                            isAnimationActive={false}
                          />
                          <Bar
                            dataKey="dataKeyB"
                            stackId="a"
                            fill={data.fillB}
                            name="Hectáreas totales de superficie"
                            radius={[0, 10, 10, 0]}
                            isAnimationActive={false}
                          />
                        </>
                      ) : (
                        <Bar
                          dataKey="dataKeyA"
                          stackId="a"
                          fill={data.fillA}
                          name="Hectáreas de café cultivadas y totales"
                          radius={10}
                          isAnimationActive={false}
                        />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="row">
                    <div className="col-md-6">
                      <i className="dot-chart" style={{ backgroundColor: data.fillA }}></i>
                      <div className="ml-4 text-left">
                        <h5 className="font-lato-normal-400 mb-0" style={{ fontSize: '1.5rem' }}>
                          {formatNumber(data.dataKeyA)}
                        </h5>
                        <h6 className="font-lato-normal-400 pt-0">Hectáreas de café cultivadas</h6>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <i className="dot-chart" style={{ backgroundColor: data.fillB }}></i>
                      <div className="ml-4 text-left">
                        <h5 className="font-lato-normal-400 mb-0" style={{ fontSize: '1.5rem' }}>
                          {data.dataKeyAB > 0 ? formatNumber(data.dataKeyAB) : formatNumber(data.dataKeyA)}
                        </h5>
                        <h6 className="font-lato-normal-400 pt-0">Hectáreas totales de superficie</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </BlockUi>
  );
};

export default ParcelasResumenGenero;
